import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { NAV, PHONE, PHONE_TEL } from "@/data/content";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const light = scrolled && !open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        open ? "border-b border-white/10 bg-[#0E2657]" : light ? "border-b border-slate-200 bg-white/90 shadow-sm backdrop-blur-xl" : "border-b border-transparent"
      }`}
    >
      <div className="container-x flex h-[84px] items-center justify-between">
        <Link to="/" data-testid="header-logo-link" aria-label="Bodhatek home">
          <Logo variant={light ? "dark" : "light"} className="h-14 w-auto sm:h-[62px]" />
        </Link>
        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.to.slice(1)}`}
              className={({ isActive }) =>
                `relative rounded-full px-4 py-2 text-sm font-medium transition-colors duration-300 ${
                  light ? (isActive ? "text-[#0E2657]" : "text-slate-600 hover:text-[#0E2657]") : isActive ? "text-white" : "text-slate-300 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && <motion.span layoutId="nav-pill" className={`absolute inset-0 rounded-full ${light ? "bg-[#0E2657]/[0.07]" : "bg-white/10"}`} />}
                  <span className="relative">{n.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="hidden items-center gap-5 lg:flex">
          <a href={`tel:${PHONE_TEL}`} data-testid="header-phone-link" className={`flex items-center gap-2 text-sm transition-colors ${light ? "text-slate-700 hover:text-[#1777AF]" : "text-slate-300 hover:text-cyan-300"}`}>
            <Phone className="h-4 w-4" /> {PHONE}
          </a>
          <Link to="/contact" data-testid="header-cta-button" className="btn-primary !py-2.5">
            Free Consultation <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
        <button
          type="button"
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((o) => !o)}
          className={`grid h-11 w-11 place-items-center rounded-full border lg:hidden ${light ? "border-slate-300 text-slate-900" : "border-white/15 text-white"}`}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden lg:hidden"
          >
            <div className="container-x flex flex-col gap-1 pb-8 pt-2">
              {NAV.map((n, i) => (
                <motion.div key={n.to} initial={{ x: -16, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.05 * i + 0.1 }}>
                  <Link to={n.to} data-testid={`mobile-nav-link-${n.to.slice(1)}`} className="block border-b border-white/10 py-4 font-display text-2xl font-semibold text-white">
                    {n.label}
                  </Link>
                </motion.div>
              ))}
              <a href={`tel:${PHONE_TEL}`} className="mt-4 flex items-center gap-2 text-slate-300" data-testid="mobile-phone-link">
                <Phone className="h-4 w-4" /> {PHONE}
              </a>
              <Link to="/contact" data-testid="mobile-cta-button" className="btn-primary mt-4">
                Get a Free Consultation
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
